import { useCallback, useEffect, useRef, useState } from 'react';
import { PAYMENT_DISPLAY_PREFIX } from '~/services/altapay/constants';
import AltaPayPayment from '~/services/altapay/payment';
import { AltaPay } from '~/services/altapay/types';
import Spinner from '~/shared/components/Spinner/Spinner';
import { getRuntimeConfig } from '~/shared/utils/config';
import { AltaPayPaymentOption, AltaPayPaymentOptionProps } from './PaymentOption';
import styles from './AltaPayScriptOption.module.scss';

type AltaPayScriptOptionProps = Omit<AltaPayPaymentOptionProps, 'children'> & {
    scriptUrl: string | undefined;
    onComplete(event: MessageEvent<string>): void;
};

const AltaPayScriptOption: React.FC<AltaPayScriptOptionProps> = ({
    scriptUrl,
    onComplete,
    ...props
}) => {
    const { isSelected, method, onChange } = props;

    const elemRef = useRef<HTMLDivElement>(null);
    const { config } = getRuntimeConfig();
    const isMounted = useRef(false);

    const [isLoading, setIsLoading] = useState(true);

    const scriptContainerId = `${PAYMENT_DISPLAY_PREFIX}-${method.id}`;

    const scrollToOption = useCallback(() => {
        if (elemRef.current) {
            const prefersReducedMotion = window.matchMedia(
                '(prefers-reduced-motion: reduce)',
            ).matches;

            scrollTo({
                top: elemRef.current.offsetTop - 120,
                behavior: prefersReducedMotion ? 'auto' : 'smooth',
            });
        }
    }, []);

    const onScriptLoad = useCallback(() => {
        setIsLoading(false);

        if (isSelected) {
            setTimeout(scrollToOption, 100);
        }
    }, [isSelected]);

    const onPaymentChange = (method: AltaPay.Schemas['PaymentMethod']) => {
        onChange(method);

        // If we already have a script and don't need to load, we scroll to it.
        if (scriptUrl) {
            setTimeout(scrollToOption, 100);
        }
    };

    useEffect(() => {
        if (!scriptUrl || isMounted.current || !isSelected) return;

        const url = new URL(scriptUrl);

        // Removes overflow being set by iframe.
        url.searchParams.append('skipBodyOverflow', 'true');

        const script = document.createElement('script');
        script.id = `script-${method.id}`;
        script.src = url.toString();

        script.onload = () => {
            setTimeout(() => {
                setIsLoading(false);

                onScriptLoad();
            }, 1000);
        };

        document.body.appendChild(script);

        isMounted.current = true;
    }, [scriptUrl]);

    useEffect(() => {
        const handleIframeMessage = (event: MessageEvent<unknown>) => {
            const isAltaPayStringMessage = (
                event: MessageEvent<unknown>,
            ): event is MessageEvent<string> =>
                event.origin === config.NEXT_PUBLIC_ALTAPAY_API_URL &&
                typeof event.data === 'string';

            if (isAltaPayStringMessage(event)) {
                const isTransactionInfo = event.data.includes('transaction_info');

                // We will only acknowledge iframe messages concerning transactions.
                if (!isTransactionInfo) return;

                // TODO(IMC-2896): Handle switching between Klarna popup and other payment options.
                onComplete(event);

                // Restart loading state once we get data back.
                setIsLoading(true);
            }
        };

        window.addEventListener('message', handleIframeMessage);

        return () => {
            window.removeEventListener('message', handleIframeMessage);
        };
    }, []);

    return (
        <AltaPayPaymentOption ref={elemRef} {...props} onChange={onPaymentChange}>
            {isLoading && (
                <div className={styles.scriptLoader}>
                    <Spinner />
                </div>
            )}

            <div
                className={styles.scriptFrame}
                id={scriptContainerId}
                data-payment-type={AltaPayPayment.getPaymentType(method)}
            />
        </AltaPayPaymentOption>
    );
};

export default AltaPayScriptOption;
